import React, { useState } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import { ClipLoader } from "react-spinners";
import Select from "react-select";

interface CreateWebhookModalProps {
  show: boolean;
  onHide: () => void;
  onSubmit: (values: {
    url: string;
    events: string[];
  }) => Promise<{ secret: string }>;
}

interface WebhookFormValues {
  url: string;
  events: string[];
}

const validationSchema = Yup.object().shape({
  url: Yup.string().url("Must be a valid URL").required("URL is required"),
  events: Yup.array()
    .of(Yup.string())
    .min(1, "Select at least one event")
    .required("Events are required"),
});

const CreateWebhookModal: React.FC<CreateWebhookModalProps> = ({
  show,
  onHide,
  onSubmit,
}) => {
  const [loading, setLoading] = useState(false);

  const eventOptions = [
    {
      value: "bank_statement.extraction.completed",
      label: "Bank Statement Extracted",
    },
    {
      value: "form.extraction.completed",
      label: "Form Extracted",
    },
    {
      value: "invoice.extraction.completed",
      label: "Invoice Extracted",
    },
    {
      value: "document.extraction.completed",
      label: "Document Extracted (any type)",
    },
  ];

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Create Webhook</Modal.Title>
      </Modal.Header>
      <Formik<WebhookFormValues>
        initialValues={{ url: "", events: ["document.extraction.completed"] }}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          setLoading(true);
          try {
            await onSubmit(values);
            onHide();
          } finally {
            setSubmitting(false);
            setLoading(false);
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Webhook URL</Form.Label>
                <Form.Control
                  type="text"
                  name="url"
                  value={values.url}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.url && !!errors.url}
                  placeholder="https://your-domain.com/webhook"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.url}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Events</Form.Label>
                <Select
                  isMulti
                  options={eventOptions}
                  value={eventOptions.filter((option) =>
                    values.events.includes(option.value)
                  )}
                  onChange={(selectedOptions) => {
                    setFieldValue(
                      "events",
                      selectedOptions
                        ? selectedOptions.map((option) => option.value)
                        : []
                    );
                  }}
                />
                {touched.events && errors.events && (
                  <div className="text-danger mt-1">{errors.events}</div>
                )}
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={onHide}>
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={isSubmitting || loading}
              >
                {loading ? (
                  <ClipLoader color="#ffffff" size={20} />
                ) : (
                  "Create Webhook"
                )}
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default CreateWebhookModal;
